import * as React from "react";
import { observer } from "mobx-react";
import { scrollLeft, scrollRight, getBannerClasses, getValueHelper, returnValueHelper } from "../../helpers";

// imported only for typings
// these are passed to the component from index.ts
import { BaseDict } from "./dict";
import { SuperBannerData } from "./data";
import { SuperBannerCMSConfiguration, SelectionType, MasterEvent, PaginationType, SiteDirection, EventTextTypeModifiers, EventInformationObject } from "../../data";

// components
import { Animate, Icon, Img, BetBtn, Loader, FreeHtml } from "sb-resp-ui";
import { eBranches } from "sb-resp-lib";
import { SuperBannerItem as SuperBannerItemModel, Market, PickedEvent } from "../../data";

// external components
import * as CMP from "./components";
import { TimestampFormatter } from "./helpers/TimeStampFormatter";

/*
    Setup testing tools for MobX
    uncoment the <DevTools /> at the end of the SuperBanner component
*/
// import DevTools from "mobx-react-devtools";
// import { setLogEnabled, setUpdatesEnabled, setGraphEnabled } from "mobx-react-devtools";
// if (process.env.NODE_ENV === "development") {
//     setLogEnabled(true);
//     setUpdatesEnabled(true);
//     setGraphEnabled(true);
// }

/* TODO(developer): refactor to lower cyclomatic complexity */
/* tslint:disable:cyclomatic-complexity */

@observer
export class SuperBanner extends React.Component<{
    dataProvider: SuperBannerData
    dict: BaseDict
    cms: SuperBannerCMSConfiguration
}, {
    dataProvider: SuperBannerData
    scrollable: {
        left: boolean
        right: boolean
    }
    anims: {
        animation: { left?: string, right?: string }
        duration: number
        easing: string | number[]
        begin?: Function
        complete?: Function
    },
    isDragging: boolean
    isClicked: boolean
}> {
    dict: BaseDict;
    dragStartX: number;
    dragDirectionFactor: 1 | -1;
    autoLoopTimeout: any;
    boundAutoLoopStart: (e: React.MouseEvent<HTMLElement>) => void;
    boundAutoLoopStop: (e: React.MouseEvent<HTMLElement>) => void;
    cms: SuperBannerCMSConfiguration;
    dirAnim: SiteDirection;
    banner__wrapper!: HTMLDivElement | null;
    banner__container!: HTMLDivElement | null;
    anims: any; // note that this.anims !== this.state.anims
    isSingleItemOrMobileView: boolean;
    private static eventsManager = new EventsManager();

    constructor(props) {
        super(props);

        // setup dictionary
        this.dict = this.props.dict;

        /* istanbul ignore next */
        this.dirAnim = this.props.dataProvider.siteDirection;

        this.anims = {
            [this.dirAnim]: 0,
            animation: {
                [this.dirAnim]: 0
            }
        };

        // setup data provider
        this.state = {
            dataProvider: this.props.dataProvider,
            scrollable: {
                left: false,
                right: false
            },
            anims: {
                animation: { ...this.anims.animation },
                duration: 350,
                easing: [10, 6],
                begin: props.dataProvider.updatePagination // always update pagination as it is the means to do autoloop
            },
            isDragging: false,
            isClicked: false
        };

        this.dragStartX = 0;
        this.dragDirectionFactor = this.dirAnim === SiteDirection.rightToLeft ? -1 : 1;
        this.cms = this.state.dataProvider.provider.dataFacade.cms;
        this.autoLoopTimeout = undefined;
        this.boundAutoLoopStart = this.autoLoopStart.bind(this);
        this.boundAutoLoopStop = this.autoLoopStop.bind(this);

        // :)
        // this.cms.UISetting.Pagination = false;
        // this.cms.UISetting.autoLoop.enabled = true;
        // this.cms.UISetting.autoLoop.duration = 1500;

        this.autoLoopPages = this.autoLoopPages.bind(this);
        this.isSingleItemOrMobileView = !this.cms.DisplayMultipleItems || this.cms.DisplayMobileMode;
    }

    /*
        AutoLoop is ignored because it re-uses already tested logic
    */
    /* istanbul ignore next */
    autoLoopPages() {
        let page = this.state.dataProvider.currentPage + 1;
        this.changePage(
            page < this.state.dataProvider.pages.length ?
                page :
                0
        )
        return this.autoLoopStart();
    }

    /* istanbul ignore next */
    autoLoopStart() {
        this.autoLoopStop();
        if (this.state.isClicked) {
            return;
        }
        return this.autoLoopTimeout = setTimeout(this.autoLoopPages, this.cms.UISetting.autoLoop.duration);
    }

    /* istanbul ignore next */
    autoLoopStop() {
        clearInterval(this.autoLoopTimeout);
        this.autoLoopTimeout = undefined;
    }

    /* istanbul ignore next */
    // handlerOnClick() {
    //     return this.state.dataProvider.reset();
    // }

    renderLoader() {
        return (
            <div className="rj-superbanner__loader">
                <Loader />
            </div>
        )
    }

    /* istanbul ignore next */
    renderBlockLoader() {
        this.resetScroll();
        return this.renderLoader();
    }

    /*
        scrollLeft() and scrollRight() are helpers and they are tested independently
        whether they return correct results
    */

    /* istanbul ignore next */
    scrollLeft() {
        let animationLeft = scrollLeft(
            this.anims,
            this.state.dataProvider.calcs,
            this.state.anims,
            this.dirAnim
        );

        if (this.state.scrollable.left) {
            this.setState(animationLeft);
            this.state.dataProvider.setCurrentPage(-1);
        }
    }

    /* istanbul ignore next */
    scrollRight() {
        let animationRight = scrollRight(
            this.anims,
            this.state.dataProvider.calcs,
            this.state.anims,
            this.dirAnim
        );

        if (this.state.scrollable.right) {
            this.setState(animationRight);
            this.state.dataProvider.setCurrentPage(1);
        }
    }

    changePageClickHandler = (e: React.MouseEvent<HTMLElement>) => {
        const target = e.target as HTMLElement;
        const index = Number(target.dataset.pageId);
        this.changePage(index);
        this.autoLoopStop();
        this.setState({
            isClicked: true
        });
    }

    changePage(x: number) {
        this.state.dataProvider.setSlideMultiplier(Math.abs(x - this.state.dataProvider.currentPage));
        x > this.state.dataProvider.currentPage ? this.scrollRight() : this.scrollLeft();
        this.state.dataProvider.updateCurrentPage(x);

        return x;
    }

    onClickArrowsHandler(direction: string, e: MouseEvent) {
        /* istanbul ignore next */
        this.autoLoopStop();
        this.setState({
            isClicked: true
        });
        return this["scroll" + direction]();
    }

    // to be used for routing between captured events
    /* istanbul ignore next */
    handleEvent(e: Event) {
        /* istanbul ignore next */
        typeof this[`on${e.type}`] === "function" && this[`on${e.type}`](e);
        if (e.type === "resize") {
            this.resetScroll();
            this.shouldScroll();
        }
    }

    /* istanbul ignore next */
    onmousedown = (e: MouseEvent) => {
        this.autoLoopStop();
        if (this.state.dataProvider.getInvisibleSpace(this.state.dataProvider.calcs) !== 0) {
            e.preventDefault();
            if (this.banner__wrapper) {
                this.banner__wrapper.addEventListener("mousemove", this);
                this.banner__wrapper.addEventListener("mouseup", this);
                this.banner__wrapper.addEventListener("mouseleave", this);
            }
            this.dragStartX = (e.pageX - (this.anims[this.dirAnim] * this.dragDirectionFactor));
        }
    }

    /* istanbul ignore next */
    onmousemove = (e: MouseEvent) => {
        e.preventDefault();
        let diff = (e.pageX - this.dragStartX) * this.dragDirectionFactor;
        if (Math.abs(diff) > 10) { // required to distinguish between drag and click events
            this.setState({
                isDragging: true
            });
            this.anims[this.dirAnim] = diff;
            window["Velocity"](
                this.banner__container,
                {
                    [this.dirAnim]: this.anims[this.dirAnim]
                },
                {
                    duration: 0
                }
            );
        }
    }

    /* istanbul ignore next */
    onmouseup = (e: MouseEvent) => {

        let progress = this.anims[this.dirAnim];
        let invisibleSpace = this.state.dataProvider.getInvisibleSpace(this.state.dataProvider.calcs);

        if (this.state.isDragging) {
            let index = this.changePage(this.state.dataProvider.getProgressPageIndex(this.state.dataProvider.calcs, progress));
            e.preventDefault();
            this.anims[this.dirAnim] = Math.min(
                Math.max(
                    -index * this.state.dataProvider.calcs.step,
                    -invisibleSpace
                ),
                0);

            this.setState(prevState => ({
                anims: {
                    ...prevState.anims,
                    animation: {
                        [this.dirAnim]: `${this.anims[this.dirAnim]}px`
                    }
                }
            }));

            if (progress > 0 || progress < -invisibleSpace) {
                // This is needed because if there isn't change in the state
                // Velocity isn't called.
                window["Velocity"](
                    this.banner__container,
                    {
                        [this.dirAnim]: this.anims[this.dirAnim]
                    },
                    {
                        duration: this.state.anims.duration
                    }
                );
            }
        }

        if (this.banner__wrapper) {
            this.banner__wrapper.removeEventListener("mousemove", this);
            this.banner__wrapper.removeEventListener("mouseup", this);
            this.banner__wrapper.removeEventListener("mouseleave", this);
        }

        this.setState({
            isDragging: false,
            isClicked: true
        });
    }

    /* istanbul ignore next */
    onmouseleave = this.onmouseup;

    /* istanbul ignore next */
    resetScroll() {
        this.anims[this.dirAnim] = 0;
        this.setState({ anims: { ...this.state.anims, animation: this.anims.animation } });
        this.state.dataProvider.setupCalcs(this.banner__wrapper, this.banner__container, this.cms.DisplayMultipleItems);
        this.state.dataProvider.currentPage = 0;
        this.state.dataProvider.updatePagination();
    }

    /* istanbul ignore next */
    shouldScroll() {
        if (this.banner__container && this.banner__wrapper) {
            let sLeft = this.anims[this.dirAnim] < 0 ? true : false;
            let sRight = this.state.dataProvider.calcs.container -
                this.state.dataProvider.calcs.wrapper + this.anims[this.dirAnim] > 0 ? true : false;
            if (
                this.state.scrollable.left !== sLeft ||
                this.state.scrollable.right !== sRight
            ) {
                this.setState({
                    scrollable: {
                        left: sLeft,
                        right: sRight
                    }
                });
            }
        }
    }

    updateCalcs() {
        this.state.dataProvider.setupCalcs(this.banner__wrapper, this.banner__container, this.cms.DisplayMultipleItems);
        this.state.dataProvider.updatePagination();
        this.shouldScroll();
    }

    /* istanbul ignore next */
    componentWillUnmount() {
        if (this.state.dataProvider.deviceDetect.isDesktop) {
            window.removeEventListener("resize", this);
            SuperBanner.eventsManager.removeEventListener("onSideVisibilityChange", this, true);
            SuperBanner.eventsManager.removeEventListener("onBranchChanged", this, true);
            SuperBanner.eventsManager.removeEventListener("onBranchRemoved", this, true);
            this.autoLoopStop();
            this.resetScroll();
            this.cms.UISetting.Draggable && this.banner__wrapper &&
                this.banner__wrapper.removeEventListener("mousedown", this);
        }
    }

    /* istanbul ignore next */
    componentDidMount() {
        if (this.state.dataProvider.deviceDetect.isDesktop) {
            this.updateCalcs();
            window.addEventListener("resize", this);
            SuperBanner.eventsManager.addEventListener("onSideVisibilityChange", this, this.updateCalcs, true);
            SuperBanner.eventsManager.addEventListener("onBranchChanged", this, this.resetScroll, true);
            SuperBanner.eventsManager.addEventListener("onBranchRemoved", this, this.resetScroll, true);
            this.cms.UISetting.autoLoop.enabled && this.autoLoopStart();
            this.cms.UISetting.Draggable && this.banner__wrapper &&
                this.banner__wrapper.addEventListener("mousedown", this);
        }
    }

    /* istanbul ignore next */
    componentDidUpdate(prevProps, prevState) {
        if (this.state.dataProvider.deviceDetect.isDesktop) {
            this.state.dataProvider.setupCalcs(this.banner__wrapper, this.banner__container, this.cms.DisplayMultipleItems);
            this.shouldScroll();
        }
    }

    /* istanbul ignore next */
    renderSuperbannerItems(items: SuperBannerItemModel[]) {
        return (
            items.length === 0 ? this.renderLoader() :
                items.map((item) => {
                    if ((this.cms.DisplayMultipleItems || this.cms.DisplayMobileMode)
                        && item.PickedEvent
                        && item.PickedEvent.IsOutright) {
                        return (
                            <CMP.SuperBannerItem
                                key={item.Id}
                                modifier={!this.cms.DisplayMobileMode ?
                                    this.state.dataProvider.getSuperBannerItemModifier(item) : ""}
                                width={this.state.dataProvider.itemWidth}>
                                {item.IsLoading && this.renderLoader()}
                                {this.renderSuperBannerEvent(item.PickedEvent)}
                            </CMP.SuperBannerItem>
                        );
                    }
                    return (<CMP.SuperBannerItem
                        key={item.Id}
                        modifier={this.state.dataProvider.getSuperBannerItemModifier(item)}
                        width={this.state.dataProvider.itemWidth}>
                        {item.IsLoading &&
                            this.renderLoader()}
                        {!this.cms.DisplayMobileMode &&
                            this.renderSuperBannerImage(item)}
                        {item.PickedEvent &&
                            this.renderSuperBannerEvent(item.PickedEvent)}
                    </CMP.SuperBannerItem>);
                })
        );
    }

    /* istanbul ignore next */
    renderSuperBannerImage(item) {
        const isPickedEv = !!item.PickedEvent && !!item.PickedEvent.event,
            IsOutright = !!item.PickedEvent && !!item.PickedEvent.IsOutright,
            isLive = isPickedEv && item.PickedEvent.event.isLive,
            hasLivestream = !!item.PickedEvent && !!item.PickedEvent.HasLivestream,
            leagueName = isPickedEv && item.PickedEvent.event.leagueName,
            height = this.state.dataProvider.getImageHeight(item, this.cms),
            anchorStyle = { height, width: "100%" };

        let link = item.PickedEvent ? item.PickedEvent.Link : "javascript:void(0);"

        return (<div className="rj-superbanner__image-row" data-uat="superbanner-image-row">
            <a className="rj-superbanner__img-link"
                href={link}
                onClick={this.state.dataProvider.provider.imageClickHandler.bind(this.state.dataProvider.provider,
                    item)}
                style={anchorStyle}
                data-uat="link-superbanner-img">
                <Img width="100%" height={height}
                    src={item.Image ? item.Image.Src : ""}
                    fallbackSrc="/imgs/i/o/superbanner/fallback.svg" />
                {(!this.isSingleItemOrMobileView && !IsOutright) && <CMP.EventText text={leagueName} modifier={[`img-${EventTextTypeModifiers.modifierLeague}`]} />}
                <div className="rj-superbaner__icon " data-uat="wrapper-superbaner-icon-live" >
                    {isLive && <CMP.LiveIndicator liveText={this.dict.LiveIndicator} />}
                    {hasLivestream && <Icon i={"i-24-16 i-o-ev-alt-streaming rj-superbaner__icon-live-stream "} />}
                </div>
            </a>
            {this.state.dataProvider.showFreeHtml(item) && <FreeHtml html={item.FreeHtmlText} />}
        </div>);
    }

    renderSuperBannerEvent(pickedEvent: PickedEvent) {
        let event: MasterEvent | undefined = pickedEvent.event;
        let market: Market | undefined = pickedEvent.market;
        let boundOpenGameTranspiler = this.state.dataProvider.provider.openGameTranspiler.bind(this.state.dataProvider.provider, pickedEvent);
        let boundOpenGameRace = this.state.dataProvider.provider.openRaceGame.bind(pickedEvent.Link);
        if (event && market) {
            let hasPossession = this.state.dataProvider.eventHasStarted(event) && event.sportID === eBranches.Tennis.toString();
            let possessionMdf = returnValueHelper(hasPossession, "possession", "");
            let outrightMdf = returnValueHelper(pickedEvent.IsOutright, "outright", "");
            let formatter = new TimestampFormatter(event, this.dict);
            let obj: EventInformationObject = {
                boundOpenGameTranspiler,
                pickedEvent,
                event,
                market,
                scoreboarProps: {
                    scoreFirstPlayer: event.scoreboard && event.scoreboard.additionalScoreData1 || [],
                    scoreSecondPlayer: event.scoreboard && event.scoreboard.additionalScoreData2 || [],
                    totalFirstPlayer: event.score1 && event.score1 || "",
                    totalSecondPlayer: event.score2 && event.score2 || "",
                    modifier: "superbanner"
                },
                hasPossession,
                onService: event.scoreboard && event.scoreboard.onServe,
                eventHasStarted: this.state.dataProvider.eventHasStarted(event),
                hasLiveScb: this.state.dataProvider.eventHasLiveScb(event),
                eventTextMdf: outrightMdf || possessionMdf,
                isMobileView: this.cms.DisplayMobileMode,
                liveIndicatorText: this.dict.LiveIndicator,
                modifierDatePeriodTimes: formatter.modifierDatePeriodTimes,
                modifierTimeScore: formatter.modifierTimeScore,
                textDatePeriodTime: formatter.textDatePeriodTime,
                textTimeScore: formatter.textTimeScore
            };
            return (
                <div className="rj-superbanner__event-container" data-uat="superbanner-event-container">
                    <CMP.EventInfo link={pickedEvent.Link} modifier={outrightMdf}
                        handler={returnValueHelper((event.sportID === eBranches.HorseRacing.toString() || event.sportID === eBranches.GreyHoundsRacing.toString()),
                            boundOpenGameRace, obj.boundOpenGameTranspiler)}>
                        {returnValueHelper(this.isSingleItemOrMobileView,
                            <CMP.EventInfoSingleItemOrMobileView {...obj} />,
                            <CMP.EventInfoMultipleItems {...obj} />
                        )}
                        {pickedEvent.IsOutright && <Icon i={`i-24 i-o-sport-${event.sportID} rj-superbanner__sport-icon`} /> || ""}
                    </CMP.EventInfo>
                    {this.renderBetButtons(pickedEvent)}
                </div>
            );
        }
        return false;
    }

    renderBetButtons(pickedEvent: PickedEvent) {
        if (pickedEvent.market && pickedEvent.market.type) {
            switch (pickedEvent.market.type) {
                case SelectionType.ML:
                    return this.renderML(pickedEvent);
                case SelectionType.HC:
                    return this.renderHC(pickedEvent);
                case SelectionType.OU:
                    return this.renderOU(pickedEvent);
                case SelectionType.QA:
                    return this.renderOutright(pickedEvent);
                default:
                    throw Error("Not implemented render method for this selection type");
            }
        }
        return false;
    }

    renderML(pickedEvent: PickedEvent) {
        let masterEvent = pickedEvent.event as MasterEvent;
        let market = pickedEvent.market as Market;
        const containerMLClass = returnValueHelper(
            this.state.dataProvider.oddsFormatter.ML.isRegular(market),
            "event-money-line-draw", "event-money-line");
        return (
            <div className={`rj-superbanner__event-row rj-superbanner__event-row--${containerMLClass}`} data-uat={`superbanner-event-row-${containerMLClass}`}>
                {market.selections.map((sel) => (<div key={sel.id} className="rj-superbanner__bet-button-wrapper" data-uat="wrapper-superbanner-bet-button">
                    <BetBtn type="bet_btn--ML"
                        modifier={this.state.dataProvider
                            .getBetBtnModifier(sel, market.type)}
                        odd={sel.odds.toString()}
                        text={this.state.dataProvider.oddsFormatter.ML.getOddName(sel, !this.cms.DisplayMultipleItems)}
                        isDisabled={sel.isDisabled}
                        handler={this.getOnBetBtnClickHandler(sel.id, masterEvent.id)} />
                </div>))}
            </div>
        );
    }

    renderHC(pickedEvent: PickedEvent) {
        let masterEvent = pickedEvent.event as MasterEvent;
        let market = pickedEvent.market as Market;
        return (
            <div className="rj-superbanner__event-row rj-superbanner__event-row--event-handicap" data-uat="superbanner-event-row-handicap">
                {market.selections
                    .slice(0, 2)
                    .map((sel) => (<div key={sel.id} className="rj-superbanner__bet-button-wrapper" data-uat="wrapper-superbanner-bet-button">
                        <BetBtn
                            type="bet_btn--HC"
                            modifier={this.state.dataProvider
                                .getBetBtnModifier(sel, market.type)}
                            point={sel.displayHandicapPoints}
                            odd={sel.odds.toString()}
                            text={sel.title}
                            isDisabled={sel.isDisabled}
                            handler={this.getOnBetBtnClickHandler(sel.id, masterEvent.id)} />
                    </div>))}
            </div>
        );
    }

    renderOU(pickedEvent: PickedEvent) {
        let masterEvent = pickedEvent.event as MasterEvent;
        let market = pickedEvent.market as Market;
        return (
            <div className="rj-superbanner__event-row rj-superbanner__event-row--event-over-under" data-uat="superbanner-event-row-over-under">
                {market.selections
                    .slice(0, 2)
                    .map((sel) => (<div key={sel.id} className="rj-superbanner__bet-button-wrapper" data-uat="wrapper-superbanner-bet-button">
                        <BetBtn type="bet_btn--OU"
                            modifier={this.state.dataProvider
                                .getBetBtnModifier(sel, market.type)}
                            point={sel.points.toString()} odd={sel.odds.toString()} text={sel.title} isDisabled={sel.isDisabled}
                            handler={this.getOnBetBtnClickHandler(sel.id, masterEvent.id)} />
                    </div>))}
            </div>
        );
    }

    renderOutright(pickedEvent: PickedEvent) {
        let event = pickedEvent.event as MasterEvent;
        let market = pickedEvent.market as Market;
        return (
            <div className="rj-superbanner__event-row rj-superbanner__event-row--event-outright" data-uat="superbanner-event-row-event-outright">
                <div className="rj-superbanner__bet-btn-container">
                    {this.state.dataProvider.oddsFormatter.Outright.getOdds(market, this.cms.DisplayMobileMode)
                        .map(sel => {
                            return (<div key={sel.id} className="rj-superbanner__bet-button-wrapper" data-uat="wrapper-superbanner-bet-button">
                                <BetBtn
                                    type="bet_btn--QA"
                                    modifier={this.state.dataProvider
                                        .getBetBtnModifier(sel, market.type)}
                                    odd={sel.odds}
                                    text={sel.title}
                                    isDisabled={sel.isDisabled}
                                    handler={this.getOnBetBtnClickHandler(sel.id, event.id)} />
                            </div>);
                        })}
                    {returnValueHelper(
                        this.state.dataProvider.oddsFormatter.Outright.showViewAll(market, this.cms.DisplayMobileMode),
                        <div className="rj-superbanner__view-all-wrapper">
                            <CMP.ViewAllButton
                                handler={
                                    returnValueHelper((event.sportID === eBranches.HorseRacing.toString() || event.sportID === eBranches.GreyHoundsRacing.toString()),
                                        this.state.dataProvider.provider.openRaceGame.bind(pickedEvent.Link),
                                        this.state.dataProvider.provider.openGameTranspiler.bind(this.state.dataProvider.provider, pickedEvent))
                                }
                                link={pickedEvent.Link}
                                text={this.dict.ViewAllButton} />
                        </div>, false)}
                </div>
            </div>
        );

    }

    /* istanbul ignore next */
    getOnBetBtnClickHandler(selectionId: string, eventId: string): Function {
        /* istanbul ignore next */
        return (() => {
            this.state.dataProvider.addToSlipTranspiler(selectionId, eventId);
        }).bind(this);
    }

    renderArrows(renderArrows) {
        const isRTL = this.dirAnim === SiteDirection.rightToLeft;
        return returnValueHelper(this.state.dataProvider.deviceDetect.isDesktop && renderArrows,
            <>
                { // (l > 0 && this.state.dataProvider.isDataLoaded) &&
                    <CMP.Arrow handler={this.onClickArrowsHandler.bind(this, returnValueHelper(isRTL, "Right", "Left"))}
                        classModifier="left" text={<Icon i="i-12 arrow-flat-left" />} />
                }
                {// (l > 0 && this.state.dataProvider.isDataLoaded) &&
                    <CMP.Arrow handler={this.onClickArrowsHandler.bind(this, returnValueHelper(isRTL, "Left", "Right"))}
                        classModifier="right" text={<Icon i="i-12 arrow-flat-right" />} />
                }
            </>, false);
    }

    renderPagination(type: PaginationType) {
        let paginationTypeDataUat = `superbanner__pager--${type}`;

        return (
            <div className={`rj-superbanner__pager rj-superbanner__pager--${type}`} data-uаt={paginationTypeDataUat}>
                {this.state.dataProvider.pages.map(page => (
                    <CMP.PaginationItem key={page.id} {...page} handler={this.changePageClickHandler} />
                ))}
            </div>
        );
    }

    /* istanbul ignore next */
    render() {

        let availItems = this.state.dataProvider.itemsToDisplay,
            isDraggingClass = this.state.isDragging ? " rj-superbanner__wrapper--is-dragging" : "",
            isDraggingDataUat = this.state.isDragging ? "wrapper-superbanner-dragging" : "wrapper-superbanner",
            hasNotFullyVisibleItems = this.state.dataProvider.getInvisibleSpace(this.state.dataProvider.calcs) !== 0;

        return availItems.length > 0 && (
            <div className={getBannerClasses({
                scrollability: this.state.scrollable,
                siteDir: this.dirAnim,
                isMultipleItemsModeEnabled: this.cms.DisplayMultipleItems,
                draggability: this.state.dataProvider.deviceDetect.isDesktop && hasNotFullyVisibleItems && this.cms.UISetting.Draggable,
                isMobileMode: this.cms.DisplayMobileMode
            })}>
                <div className="rj-superbanner__wrapper-shell"
                    data-uat="wrapper-superbanner-shell"
                    onMouseEnter={getValueHelper(this.cms.UISetting.autoLoop.enabled, this.boundAutoLoopStop)}
                    onMouseLeave={getValueHelper(this.cms.UISetting.autoLoop.enabled, this.boundAutoLoopStart)}>
                    <div className={`rj-superbanner__wrapper${isDraggingClass}`} ref={n => this.banner__wrapper = n} data-uat={isDraggingDataUat}>
                        <Animate {...this.state.anims}>
                            <div className="rj-superbanner__container" ref={n => this.banner__container = n}>
                                <div className="rj-superbanner__slider">
                                    {this.renderSuperbannerItems(availItems)}
                                </div>
                            </div>
                        </Animate>
                    </div>
                    {this.renderArrows(hasNotFullyVisibleItems && this.cms.UISetting.useArrows)}
                </div>
                {
                    this.state.dataProvider.deviceDetect.isDesktop && this.cms.UISetting.Pagination &&
                    hasNotFullyVisibleItems &&
                    this.renderPagination(this.cms.DisplayMultipleItems ? PaginationType.lines : PaginationType.dots)
                }
                {/* <DevTools /> */}
            </div>
        );
    }
}